import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { Table } from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";
import { Box, Pagination } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { 
  format, 
  parseISO, 
  isWithinInterval, 
  startOfDay, 
  endOfDay,
  startOfMonth,
  endOfMonth,
  format as formatDate
} from "date-fns";
import * as Actions from "../../store/action/inspectorShift";
import { fetchUser } from "../../store/action/user";
import { getPartyDetails } from "../../store/action/party";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    minHeight: "85vh",
  },
  paper: {
    width: "100%",
    borderRadius: "11px",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.06)",
  },
  table: {
    minWidth: 650,
  },
  tableWrapper: {
    padding: "40px",
  },
  tableHeadRows: {
    whiteSpace: "nowrap",
    fontFamily: "Poppins",
    fontWeight: 600,
    fontSize: 12,
    lineHeight: "18px",
    color: "rgba(181, 181, 195, 1)",
    border: 0,
    background: "rgba(243, 246, 249, 1)",
    padding: "1%",
  },
  titre: {
    fontFamily: "Poppins",
    fontSize: "13.8px",
    fontWeight: 600,
    lineHeight: "20.7px",
    color: "rgba(63, 66, 84, 1)",
  },
  titreLabel: {
    marginLeft: "10px",
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "18px",
    color: "rgba(32, 18, 70, 1)",
  },
  selectInput: {
    border: "0.61px solid rgba(226, 217, 239, 1)",
    background: "rgba(255, 255, 255, 1)",
    width: "180px",
    height: "34px",
    borderRadius: 5,
    marginLeft: "20px",
  },
  optionItem: {
    width: '150px',
    padding: theme.spacing(1),
    cursor: 'pointer',
    backgroundColor: 'white',
    color: 'rgba(32, 18, 70, 1)',
    fontFamily: 'Poppins',
    fontSize: '10px',
    fontWeight: 400,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  dateRangeContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginLeft: "20px",
  },
  dateInput: {
    border: "0.61px solid rgba(226, 217, 239, 1)",
    background: "rgba(255, 255, 255, 1)",
    width: "140px",
    height: "29.69px",
    borderRadius: 5,
    padding: "0 10px",
    fontFamily: "Poppins",
    fontSize: "12px",
    color: "rgba(32, 18, 70, 1)",
    "&:focus": {
      outline: "none",
      borderColor: "rgba(187, 200, 8, 1)",
    },
  },
  dateSeparator: {
    color: "rgba(32, 18, 70, 1)",
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: 500,
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "20px",
    padding: "0 20px",
  },
  pagination: {
    '& .MuiPaginationItem-root': {
      fontFamily: 'Poppins',
      fontSize: '12px',
      color: 'rgba(32, 18, 70, 1)',
      '&.Mui-selected': {
        backgroundColor: 'rgba(240, 243, 199, 1)',
        color: 'rgba(60, 101, 86, 1)',
        '&:hover': {
          backgroundColor: 'rgba(240, 243, 199, 0.8)',
        },
      },
    },
  },
}));

export default function InspectorShifts() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { shifts, loading } = useSelector((state) => state.inspectorShift);
  const [user, setUser] = useState();
  const [partyDetails, setPartyDetails] = useState({});
  
  // Initialize with current month's start and end dates
  const [startDate, setStartDate] = useState(formatDate(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(formatDate(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [selectedInspector, setSelectedInspector] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  const formatTime = (time) => {
    return format(new Date(time), "HH:mm");
  };

  const fetchShifts = async (associatedRepairShop) => {
    if (!associatedRepairShop) {
      console.error("Missing associatedRepairShop in query.");
      return;
    }
    try {
      const result = await dispatch(Actions.fetchInspectorShiftsWithGarage(associatedRepairShop));
      // Log a message if no shifts were found
      if (!result || result.length === 0) {
        console.log("No inspector shifts found for this garage.");
      }
    } catch (error) {
      console.error("Error fetching shifts with the Garage:", error);
      // Continue execution even if there's an error
    }
  };

  const fetchPartyDetails = async (partyId) => {
    try {
        console.log("partyId", partyId);
        const response = await getPartyDetails(partyId);
        console.log("response", response);
        return response;
    } catch (error) {
        console.error("Error fetching party details:", error);
        return null;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const userResponse = await dispatch(fetchUser());
        setUser(userResponse);

        if (userResponse?.associatedPartyUid) {
          await fetchShifts(userResponse.associatedPartyUid);
        } else {
          console.error("User does not have an associatedPartyUid.");
        }
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    
    loadData();
  }, [dispatch]);

  useEffect(() => {
    const loadPartyDetails = async () => {
      if (Array.isArray(shifts) && shifts.length > 0) {
        const uniqueInspectors = [...new Set(shifts.map(shift => shift.associatedInspector))];
        
        for (const inspectorId of uniqueInspectors) {
          if (inspectorId && !partyDetails[inspectorId]) {
            const details = await fetchPartyDetails(inspectorId);
            if (details) {
              setPartyDetails(prev => ({
                ...prev,
                [inspectorId]: details
              }));
            }
          }
        }
      }
    };

    loadPartyDetails();
  }, [shifts]);

  const groupShiftsByDate = (shifts) => {
    // Ensure shifts is an array before reducing
    if (!Array.isArray(shifts) || shifts.length === 0) {
      return {};
    }

    return shifts.reduce((groups, shift) => {
      const date = format(new Date(shift.date), "dd/MM/yyyy");
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(shift);
      return groups;
    }, {});
  };
  
  // Apply date and inspector filters
  const applyFilters = (shifts) => {
    // Ensure shifts is an array before filtering
    if (!Array.isArray(shifts)) {
      return [];
    }
    
    return shifts.filter(shift => {
      // Make sure the shift has a date property
      if (!shift.date) {
        return false;
      }

      try {
        const shiftDate = new Date(shift.date);
        
        const start = startDate ? startOfDay(new Date(startDate)) : null;
        const end = endDate ? endOfDay(new Date(endDate)) : null;
        
        const dateMatch = (!start || !end) ? true : isWithinInterval(shiftDate, { start, end });
        const inspectorMatch = !selectedInspector || shift.associatedInspector === selectedInspector;
        
        return dateMatch && inspectorMatch;
      } catch (error) {
        console.error('Error filtering shift:', error, shift);
        return false;
      }
    });
  };

  // Group and filter shifts directly from the Redux store
  const filteredShifts = applyFilters(shifts);
  const filteredAndGroupedShifts = groupShiftsByDate(filteredShifts);
  
  // Get unique dates for pagination
  const uniqueDates = Object.keys(filteredAndGroupedShifts);
  const totalPages = Math.ceil(uniqueDates.length / itemsPerPage);
  
  // Get dates for current page
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageDates = uniqueDates.slice(startIndex, endIndex);
  
  // Create paginated shifts object
  const paginatedShifts = currentPageDates.reduce((acc, date) => {
    acc[date] = filteredAndGroupedShifts[date];
    return acc;
  }, {});

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedInspector]);

  // Get unique inspectors for the inspector filter directly from shifts
  const uniqueInspectors = Array.isArray(shifts) ? [...new Set(shifts.map(shift => shift.associatedInspector))] : [];

  // Effect to log debugging information for date ranges
  useEffect(() => {
    if (Array.isArray(shifts) && shifts.length > 0) {
      console.log('Current shifts data:', shifts);
      console.log('Start date filter:', startDate, new Date(startDate));
      console.log('End date filter:', endDate, new Date(endDate));
      
      // Test if the dates in the shifts are within the range
      shifts.forEach(shift => {
        const shiftDate = new Date(shift.date);
        const start = startDate ? startOfDay(new Date(startDate)) : null;
        const end = endDate ? endOfDay(new Date(endDate)) : null;
        
        if (start && end) {
          const isInRange = isWithinInterval(shiftDate, { start, end });
          console.log(
            `Shift date ${format(shiftDate, 'yyyy-MM-dd')} in range ${format(start, 'yyyy-MM-dd')} to ${format(end, 'yyyy-MM-dd')}: ${isInRange}`
          );
        }
      });
    }
    
    return () => {
      // Cleanup function to clear console after debugging
      console.clear();
    };
  }, [shifts, startDate, endDate]);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.tableWrapper}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginTop: "20px",
              marginBottom: "40px",
              whiteSpace: "nowrap",
            }}
          >
            <div className={classes.titre} style={{ width: "40%" }}>
              Liste des experts plannifiés 
            </div>
            <div className={classes.titre} style={{ width: "10%" }}>
              Filtrer par
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "45%",
                justifyContent: "flex-start",
              }}
            >
              <div className={classes.titreLabel}>
                Période
              </div>
              <div className={classes.dateRangeContainer}>
                <input
                  type="date"
                  className={classes.dateInput}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <span className={classes.dateSeparator}>-</span>
                <input
                  type="date"
                  className={classes.dateInput}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className={classes.titreLabel}>
                Expert
                <select
                  className={classes.selectInput}
                  value={selectedInspector}
                  onChange={(e) => setSelectedInspector(e.target.value)}
                >
                  <option className={classes.optionItem} value="">
                        Sélectionnez un expert
                  </option>
                  {uniqueInspectors.map((inspectorId) => (
                    <option
                      className={classes.optionItem}
                      key={inspectorId}
                      value={inspectorId}
                    >
                      {partyDetails[inspectorId] 
                        ? `${partyDetails[inspectorId].name} ${partyDetails[inspectorId].lastName}`
                        : inspectorId}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div
              style={{
                alignItems: "center",
                width: "100%",
                paddingRight: "20px",
              }}
            >
              <Table className={classes.table} aria-labelledby="tableTitle" >
                <thead>
                  <tr>
                    <TableCell
                      height={"43px"}
                      align="left"
                      className={classes.tableHeadRows}
                      style={{
                        borderTopLeftRadius: "6px",
                        borderBottomLeftRadius: "6px",
                      }}
                    >
                            Expert
                    </TableCell>
                    <TableCell
                      height={"43px"}
                      align="right"
                      className={classes.tableHeadRows}
                      style={{
                        borderTopRightRadius: "6px",
                        borderBottomRightRadius: "6px",
                      }}
                    >
                      Horaire
                    </TableCell>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <TableCell colSpan="2" style={{ textAlign: "center", padding: "30px" }}>
                        <div style={{
                          fontFamily: "Poppins",
                          fontSize: "14px",
                          color: "rgba(60, 101, 86, 1)",
                          fontWeight: 500
                        }}>
                          Chargement des données...
                        </div>
                      </TableCell>
                    </tr>
                  ) : Object.keys(paginatedShifts).length > 0 ? (
                    Object.entries(paginatedShifts).map(([date, dateShifts]) => (
                      <React.Fragment key={date}>
                        <tr>
                          <TableCell colSpan="2" style={{ border: 0 }}>
                            <Accordion allowMultiple defaultIndex={[0]}>
                              <AccordionItem>
                                <AccordionButton
                                  style={{
                                    height: "47px",
                                    border: "none",
                                    background: "rgba(255, 255, 255, 1)",
                                    borderBottom: "1px solid rgba(224, 224, 224, 1)",
                                  }}
                                >
                                  <AccordionIcon
                                    size="30px"
                                    style={{
                                      backgroundColor: "rgba(240, 243, 199, 1)",
                                      width: "25px",
                                      height: "25px",
                                      borderRadius: "5px",
                                      color: "rgba(187, 200, 8, 1)",
                                    }}
                                  />
                                  <Box
                                    as="div"
                                    textAlign="left"
                                    marginLeft="20px"
                                    style={{
                                      color: "rgba(60, 101, 86, 1)",
                                      fontSize: "14px",
                                      fontWeight: 500,
                                      lineHeight: "21px",
                                      fontFamily: "Poppins",
                                    }}
                                  >
                                    {date}
                                  </Box>
                                </AccordionButton>
                                <AccordionPanel
                                  style={{
                                    background: "rgba(255, 255, 255, 1)",
                                    borderRadius: "11px",
                                  }}
                                >
                                  {dateShifts.map((shift) => (
                                    <div
                                      key={shift._id}
                                      style={{
                                        padding: "10px 3px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        background: "rgba(249, 249, 249, 1)",
                                        borderBottom: "1px solid rgb(224, 224, 224)",
                                        color: "#464E5F",
                                        fontSize: "12px",
                                        fontWeight: 400,
                                        lineHeight: "21px",
                                        fontFamily: "Poppins",
                                        alignItems: "center",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: "25%",
                                          textAlign: "left",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "inline-block",
                                            width: "auto",
                                            minWidth: "120px",
                                            textAlign: "center",
                                            padding: "5px 10px",
                                            borderRadius: "51px",
                                            border: 0,
                                            fontFamily: "Poppins",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            lineHeight: "16.03px",
                                            color: "rgba(60, 101, 86, 1)",
                                            backgroundColor: "rgba(240, 243, 199, 1)",
                                          }}
                                        >
                                          {partyDetails[shift.associatedInspector] 
                                            ? `${partyDetails[shift.associatedInspector].name} ${partyDetails[shift.associatedInspector].lastName}`
                                            : shift.associatedInspector}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          width: "15%",
                                          textAlign: "right",
                                          color: "rgba(60, 101, 86, 1)",
                                          fontSize: "14px",
                                          fontWeight: 600,
                                          lineHeight: "21px",
                                          fontFamily: "Poppins",
                                        }}
                                      >
                                        {formatTime(shift.startDate)} - {formatTime(shift.endDate)}
                                      </div>
                                    </div>
                                  ))}
                                </AccordionPanel>
                              </AccordionItem>
                            </Accordion>
                          </TableCell>
                        </tr>
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <TableCell colSpan="2" style={{ textAlign: "center", padding: "30px" }}>
                        <div style={{
                          fontFamily: "Poppins",
                          fontSize: "14px",
                          color: "rgba(60, 101, 86, 1)",
                          fontWeight: 500
                        }}>
                          Aucun expert programmé pour ce garage.
                        </div>
                      </TableCell>
                    </tr>
                  )}
                </tbody>
              </Table>
              
              {totalPages > 1 && (
                <div className={classes.paginationContainer}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    className={classes.pagination}
                    sx={{
                      '& .MuiPagination-ul': {
                        gap: '4px',
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Paper>
    </div>
  );
} 